import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Helmet } from 'react-helmet-async';

import config from '@src/config';
import { toast } from '@src/components/core/toaster';
import { fetchClient } from '@src/api/actions/clients';
import leadGurusLogo from '@src/assets/logo-full.png';
import { fetchProjectionSummary, fetchProjectionZips, fetchProjectionZipDemographics } from '@src/api/actions/projections';
import { fetchVerticals } from '@src/api/actions/verticals';

const ZONE_TEMPLATE_ROWS = [
  { zip: '60601', territory: 'Downtown' },
  { zip: '60614', territory: 'North' },
];

const DEFAULT_STATE = {
  territories: {
    text: '',
    fileName: '',
    parsed: [],
  },
  verticals: {
    selections: [],
    applyToAllZips: true,
    budgets: {},
  },
  projection: {
    scenario: 'base',
    budget: 0,
    summary: null,
    zipRows: [],
  },
};

function parseZips(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [zip = '', territory = ''] = line.split(/[,|\t]/);
      let normalizedZip = zip.trim();
      if (/^\d{4}$/.test(normalizedZip)) {
        normalizedZip = `0${normalizedZip}`;
      }
      return { zip: normalizedZip, territory: territory.trim() };
    })
    .filter((row) => row.zip);
}

function PublicProjectionsPage() {
  const { clientSlug = 'new-client' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(DEFAULT_STATE);
  const [client, setClient] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [isClientError, setIsClientError] = useState(false);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);
  const [isLoadingVerticals, setIsLoadingVerticals] = useState(false);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [demographics, setDemographics] = useState({ territories: [], totals: null, meta: null });
  const scenarioMultipliers = { low: 0.9, base: 1, high: 1.2 };

  useEffect(() => {
    setState((prev) => ({ ...prev, projection: { ...DEFAULT_STATE.projection } }));
    if (!clientSlug) {
      setClient(null);
      return;
    }
    setIsLoadingClient(true);
    setIsClientError(false);
    fetchClient(clientSlug)
      .then((response) => {
        setClient(response?.data || null);
      })
      .catch(() => {
        setClient(null);
        setIsClientError(true);
      })
      .finally(() => setIsLoadingClient(false));
  }, [clientSlug]);

  useEffect(() => {
    if (client?.slug && clientSlug && clientSlug !== client.slug) {
      navigate(`/projections/${client.slug}${location.search}`, { replace: true });
    }
  }, [client?.slug, clientSlug, location.search, navigate]);

  useEffect(() => {
    const zipsParam = searchParams.get('zips');
    const verticalsParam = searchParams.get('verticals');
    const budgetParam = searchParams.get('budget');
    const scenarioParam = searchParams.get('scenario');

    setState((prev) => {
      const next = { ...prev, verticals: { ...prev.verticals }, projection: { ...prev.projection } };
      if (verticalsParam) {
        next.verticals.selections = verticalsParam.split(',').filter(Boolean);
      }
      if (budgetParam) {
        next.projection.budget = Number(budgetParam) || prev.projection.budget;
      }
      if (scenarioParam) {
        next.projection.scenario = scenarioParam;
      }
      if (zipsParam) {
        try {
          const parsed = JSON.parse(zipsParam);
          const text = parsed
            .map((row) => [row.zip, row.territory].filter(Boolean).join(', '))
            .join('\n');
          next.territories = { ...prev.territories, parsed, text };
        } catch (e) {
          // ignore malformed
        }
      }
      return next;
    });
  }, [searchParams]);

  useEffect(() => {
    setIsLoadingVerticals(true);
    fetchVerticals()
      .then((response) => {
        setVerticalOptions(response?.data || []);
      })
      .catch(() => {
        toast.error('Could not load verticals. Please refresh and try again.');
      })
      .finally(() => setIsLoadingVerticals(false));
  }, []);

  const updateState = (path, value) => {
    setState((prev) => {
      const next = { ...prev };
      const segments = path.split('.');
      let cursor = next;
      segments.slice(0, -1).forEach((segment) => {
        cursor[segment] = { ...cursor[segment] };
        cursor = cursor[segment];
      });
      cursor[segments.slice(-1)[0]] = value;
      return next;
    });
  };

  const handleZipTextChange = (value) => {
    setState((prev) => ({
      ...prev,
      territories: {
        ...prev.territories,
        text: value,
        parsed: parseZips(value),
      },
    }));
  };

  const handleZipFile = (files) => {
    if (!files || !files.length) return;
    const [file] = files;
    const reader = new FileReader();
    reader.onload = (event) => {
      const contents = event.target?.result || '';
      setState((prev) => ({
        ...prev,
        territories: {
          ...prev.territories,
          text: contents,
          parsed: parseZips(String(contents)),
          fileName: file.name,
        },
      }));
    };
    reader.readAsText(file);
  };

  const handleProjectionFetch = () => {
    setIsLoadingProjection(true);
    const selectedVerticals = (state.verticals.selections || []).filter(Boolean);
    const baseZipRows = state.territories.parsed || [];
    const zipRows =
      selectedVerticals.length === 0
        ? baseZipRows.map((row) => ({ zip: row.zip, territory: row.territory }))
        : selectedVerticals.flatMap((vertical) =>
            baseZipRows.map((row) => ({
              zip: row.zip,
              territory: row.territory,
              vertical,
            }))
          );
    const zipList = Array.from(new Set(zipRows.map((row) => row.zip))).filter(Boolean);
    const verticalBudgets = {};
    selectedVerticals.forEach((v) => {
      verticalBudgets[v] = Number(state.verticals.budgets?.[v] ?? 0);
    });
    const totalBudget = Object.values(verticalBudgets).reduce((sum, val) => sum + (Number(val) || 0), 0);

    const payload = {
      verticals: selectedVerticals,
      zips: zipList,
      zip_rows: zipRows,
      budget: totalBudget || state.projection.budget,
      vertical_budgets: verticalBudgets,
      scenario: 'base',
      vertical: selectedVerticals[0] || '',
      client_slug: clientSlug,
    };
    Promise.all([fetchProjectionSummary(payload), fetchProjectionZips(payload), fetchProjectionZipDemographics(payload)])
      .then(([summaryResp, zipResp, demoResp]) => {
        setState((prev) => ({
          ...prev,
          projection: {
            ...prev.projection,
            summary: summaryResp?.data?.summary || null,
            zipRows: zipResp?.data?.rows || [],
          },
        }));
        setDemographics({
          territories: demoResp?.data?.territories || [],
          totals: demoResp?.data?.totals || null,
          meta: demoResp?.data?.meta || null,
        });
      })
      .catch(() => {
        toast.error('Could not load projections. Please try again.');
      })
      .finally(() => {
        setIsLoadingProjection(false);
      });
  };

const verticalRollup = useMemo(() => {
  const totals = new Map();
  (state.projection.zipRows || []).forEach((row) => {
    const key = row.vertical || 'Unknown';
    const current = totals.get(key) || { zipCount: 0, estLeads: 0, avgCpl: 0 };
      current.zipCount += 1;
      current.estLeads += Number(row.est_leads || 0);
      current.avgCpl += Number(row.avg_cpl || 0);
      totals.set(key, current);
    });
    return Array.from(totals.entries()).map(([vertical, data]) => ({
      vertical,
      zipCount: data.zipCount,
      estLeads: data.estLeads,
      avgCpl: data.zipCount ? Math.round((data.avgCpl / data.zipCount) * 100) / 100 : 0,
    }));
  }, [state.projection.zipRows]);

  const territoryRollup = useMemo(() => {
    const counts = new Map();
    (state.territories.parsed || []).forEach((row) => {
      const key = row.territory || 'Unknown';
      const current = counts.get(key) || { zips: [] };
      current.zips.push(row.zip);
      counts.set(key, current);
    });
    return Array.from(counts.entries()).map(([territory, data]) => ({
      territory,
      count: data.zips.length,
      sampleZips: data.zips.slice(0, 6),
    }));
  }, [state.territories.parsed]);

  const territoryRows = useMemo(() => {
    const rows = [];
    const demoMap = new Map();
    (demographics.territories || []).forEach((row) => {
      demoMap.set(row.territory || 'Unknown', row);
    });

    const keys = new Set([...(demographics.territories || []).map((t) => t.territory || 'Unknown')]);
    keys.forEach((key) => {
      const demo = demoMap.get(key);
      rows.push({
        territory: key,
        zipCount: demo?.zip_count ?? 0,
        sampleZips: demo?.sample_zips || [],
        population: demo?.population ?? null,
        populationFiltered: demo?.population_filtered ?? null,
        populationRetainedPct: demo?.population_retained_pct ?? null,
        medianIncome: demo?.median_income ?? null,
        medianIncomeFiltered: demo?.median_income_filtered ?? null,
        medianHomeValue: demo?.median_home_value ?? null,
        medianHomeValueFiltered: demo?.median_home_value_filtered ?? null,
        zipCountFiltered: demo?.zip_count_filtered ?? null,
        minIncome: demo?.min_household_income ?? null,
        minHomeValue: demo?.min_home_value ?? null,
        minSfh: demo?.min_sfh_percentage ?? null,
        minOwnership: demo?.min_home_ownership_percentage ?? null,
      });
    });
    return rows;
  }, [demographics]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  };

  const formatPercent = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return `${num.toFixed(decimals)}%`;
  };

  const completion = useMemo(() => {
    const basicsComplete = Boolean(state.territories.parsed.length && state.verticals.selections.length);
    return { basics: basicsComplete };
  }, [state.territories.parsed.length, state.verticals.selections.length]);

  const progressValue = useMemo(
    () => Math.round((Object.values(completion).filter(Boolean).length / Object.keys(completion).length) * 100),
    [completion]
  );

  const proceedToOnboarding = () => {
    const params = new URLSearchParams();
    if (state.verticals.selections.length) {
      params.set('verticals', state.verticals.selections.join(','));
    }
    if (state.projection.budget) params.set('budget', state.projection.budget);
    if (state.projection.scenario) params.set('scenario', state.projection.scenario);
    if (state.territories.parsed.length) {
      params.set('zips', JSON.stringify(state.territories.parsed));
    }
    const search = params.toString();
    const targetSlug = client?.slug || clientSlug || '';
    navigate(`/onboarding/${targetSlug}${search ? `?${search}` : ''}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Helmet>
        <title>Projections | {config.SITE.title}</title>
      </Helmet>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #f66f39, #f88f5f)',
                borderRadius: 2,
                color: 'common.white',
                p: { xs: 2, md: 3 },
                boxShadow: 4,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: { md: 'center', xs: 'flex-start' }, justifyContent: 'space-between' }}
              >
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box
                    component="img"
                    src={leadGurusLogo}
                    alt="Lead Gurus"
                    sx={{
                      height: 64,
                      width: 'auto',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.2))',
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Lead Gurus Projections
                  </Typography>
                  {client?.client_logo_url || client?.client_logo_file ? (
                    <Box
                      component="img"
                      src={client?.client_logo_url || client?.client_logo_file}
                      alt={client?.name || 'Client logo'}
                      sx={{
                        height: 48,
                        width: 'auto',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{ width: 48, height: 48, bgcolor: 'white', color: 'text.primary', fontWeight: 700 }}
                    >
                      {client?.name ? client.name.slice(0, 2).toUpperCase() : 'CL'}
                    </Avatar>
                  )}
                </Stack>
                <Stack spacing={1} sx={{ minWidth: { md: 260, xs: '100%' } }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <RocketLaunchIcon color="primary" fontSize="small" />
                    <Typography variant="body2">Inputs readiness</Typography>
                    <Chip color="primary" label={`${progressValue}%`} size="small" />
                  </Stack>
                  <LinearProgress
                    aria-label="Projection readiness"
                    sx={{ height: 10, borderRadius: 999 }}
                    value={progressValue}
                    variant="determinate"
                  />
                </Stack>
              </Stack>
            </Box>
            {isClientError && (
              <Alert severity="warning">
                Could not load client or you do not have access to this client.
              </Alert>
            )}

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Service areas & verticals
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Paste zip codes and territories"
                      helperText="One per line, format: zip_code, territory"
                      minRows={6}
                      maxRows={8}
                      multiline
                      value={state.territories.text}
                      onChange={(e) => handleZipTextChange(e.target.value)}
                      sx={{ textarea: { overflow: 'auto' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1.5}>
                      <Button component="label" startIcon={<UploadFileIcon />} variant="outlined">
                        Upload zip template
                        <input
                          hidden
                          type="file"
                          accept=".csv,.txt"
                          onChange={(e) => handleZipFile(e.target.files)}
                        />
                      </Button>
                      {state.territories.fileName ? (
                        <Typography variant="body2">Uploaded: {state.territories.fileName}</Typography>
                      ) : (
                        <Typography color="text.secondary" variant="body2">
                          Template columns: zip_code, territory
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Typography color="text.secondary" variant="caption">
                        Example format
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {ZONE_TEMPLATE_ROWS.map((row) => (
                          <Chip key={row.zip} label={`${row.zip} — ${row.territory}`} size="small" />
                        ))}
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      SelectProps={{ multiple: true, disabled: isLoadingVerticals }}
                      label="Verticals to model"
                      value={state.verticals.selections}
                      onChange={(e) => updateState('verticals.selections', e.target.value)}
                      helperText={
                        isLoadingVerticals
                          ? 'Loading verticals…'
                          : 'We’ll use these verticals for projections.'
                      }
                    >
                      {verticalOptions.length ? (
                        verticalOptions.map((vertical) => (
                          <MenuItem key={vertical.id || vertical.vertical} value={vertical.vertical}>
                            {vertical.vertical}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled value="">
                          {isLoadingVerticals ? 'Loading…' : 'No verticals available'}
                        </MenuItem>
                      )}
                    </TextField>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.verticals.applyToAllZips}
                          onChange={(e) => updateState('verticals.applyToAllZips', e.target.checked)}
                        />
                      }
                      label="Apply these verticals to all zip codes"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ alignItems: { sm: 'center', xs: 'flex-start' }, justifyContent: 'space-between' }}
                  >
                    <Typography variant="h6">Projections</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={state.projection.scenario}
                      onChange={(e, value) => value && updateState('projection.scenario', value)}
                    >
                      <ToggleButton value="low">Low</ToggleButton>
                      <ToggleButton value="base">Base</ToggleButton>
                      <ToggleButton value="high">High</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Budgets by vertical</Typography>
                    {state.verticals.selections.length ? (
                      <Grid container spacing={2}>
                        {state.verticals.selections.map((vertical) => (
                          <Grid item xs={12} sm={6} md={4} key={vertical}>
                            <TextField
                              fullWidth
                              type="number"
                              label={`${vertical} budget`}
                              InputProps={{ startAdornment: '$', inputProps: { min: 0, step: 100 } }}
                              value={state.verticals.budgets?.[vertical] ?? ''}
                              onChange={(e) =>
                                updateState('verticals.budgets', {
                                  ...state.verticals.budgets,
                                  [vertical]: e.target.value,
                                })
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        Select at least one vertical to enter budgets.
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleProjectionFetch}
                      disabled={isLoadingProjection || !state.verticals.selections.length}
                    >
                      {isLoadingProjection ? 'Calculating…' : 'Generate projections'}
                    </Button>
                    <Typography color="text.secondary" variant="body2">
                      Uses pasted/uploaded zips and selected verticals for estimates.
                    </Typography>
                  </Stack>

                  {state.projection.summary ? (
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                      {(() => {
                          const summary = state.projection.summary || {};
                          const budget = Number(summary.budget) || 0;
                          const dailyBudget = budget ? budget / 30 : null;
                          const baseCpl = summary.assumed_cpl != null ? Number(summary.assumed_cpl) : 85;
                          const currentScenario = state.projection.scenario || 'base';
                          const mult = scenarioMultipliers[currentScenario] || 1;
                          const scenarioMetrics = Object.entries(scenarioMultipliers).reduce((acc, [key, m]) => {
                            const cplVal = baseCpl * m;
                            const leadsVal = cplVal ? budget / cplVal : 0;
                            acc[key] = {
                              cpl: cplVal,
                              leads: leadsVal,
                              setAppointments: leadsVal * 0.6,
                              issues: leadsVal * 0.1,
                              demos: leadsVal * 0.5,
                              dealsSold: leadsVal * 0.2,
                            };
                            return acc;
                          }, {});
                          const current = scenarioMetrics[currentScenario] || scenarioMetrics.base || {};
                          const leads = Number.isFinite(current.leads) ? Math.round(current.leads) : 0;
                          const cpl = current.cpl != null ? current.cpl : '—';
                          const setAppointments = Number.isFinite(current.setAppointments)
                            ? Math.round(current.setAppointments)
                            : '—';
                          const issues = Number.isFinite(current.issues) ? Math.round(current.issues) : '—';
                          const demos = Number.isFinite(current.demos) ? Math.round(current.demos) : '—';
                          const dealsSold = Number.isFinite(current.dealsSold) ? Math.round(current.dealsSold) : null;
                          const costMarketingPct = '20%';
                          const avgJobSize = 12000;
                          const netRevenue = dealsSold != null ? dealsSold * avgJobSize : null;

                          return [
                            {
                              label: 'Total budget',
                              value: budget ? `$${budget.toLocaleString()}` : '—',
                              sub: dailyBudget ? `~$${dailyBudget.toLocaleString(undefined, { maximumFractionDigits: 0 })}/day` : null,
                            },
                            {
                              label: `Leads & CPL (${currentScenario})`,
                              value: `${leads || '—'} leads / $${cpl}`,
                              sub: 'Est. leads / Assumed CPL',
                            },
                            { label: 'Set appointments', value: setAppointments, sub: 'Set rate 25%' },
                            { label: 'Issues', value: issues, sub: 'Issue rate 65%' },
                            { label: 'Demos', value: demos, sub: 'Demo rate 50%' },
                            { label: 'Net sold', value: dealsSold == null ? '—' : dealsSold.toLocaleString(), sub: 'Net close rate 20%' },
                            { label: 'Avg job size', value: `$${avgJobSize.toLocaleString()}`, sub: null },
                            { label: 'Net revenue', value: netRevenue == null ? '—' : `$${netRevenue.toLocaleString()}`, sub: null },
                            { label: 'Cost of marketing', value: costMarketingPct, sub: null },
                          ];
                        })().map((item) => (
                          <Grid item xs={12} sm={6} md={4} key={item.label}>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                              <Typography color="text.secondary" variant="body2">
                                {item.label}
                              </Typography>
                              <Typography variant="h6">{item.value ?? '—'}</Typography>
                              {item.sub ? (
                                <Typography color="text.secondary" variant="caption">
                                  {item.sub}
                                </Typography>
                              ) : null}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>

                      {state.verticals.selections.length ? (
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Table
                            size="small"
                            sx={{
                              tableLayout: 'fixed',
                              '& th, & td': { px: 0.5, py: 0.75, whiteSpace: 'nowrap', fontSize: 12 },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell>Vertical</TableCell>
                                <TableCell>Budget</TableCell>
                                <TableCell>Leads</TableCell>
                                <TableCell>CPL</TableCell>
                                <TableCell>Set</TableCell>
                                <TableCell>Set%</TableCell>
                                <TableCell>Issues</TableCell>
                                <TableCell>Iss%</TableCell>
                                <TableCell>Demos</TableCell>
                                <TableCell>Demo%</TableCell>
                                <TableCell>Net sold</TableCell>
                                <TableCell>Close%</TableCell>
                                <TableCell>Avg job</TableCell>
                                <TableCell>Net rev</TableCell>
                                <TableCell>CoM%</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {state.verticals.selections.map((vertical) => {
                                const avgJobSize = 12000;
                                const verticalBudget = Number(state.verticals.budgets?.[vertical]) || 0;
                                const baseCplVal =
                                  state.projection.summary?.assumed_cpl != null
                                    ? Number(state.projection.summary.assumed_cpl)
                                    : 85;
                                const mult = scenarioMultipliers[state.projection.scenario || 'base'] || 1;
                                const cplVal = baseCplVal * mult;
                                const leadsVal = cplVal ? Math.round(verticalBudget / cplVal) : 0;
                                const setApptsVal = Math.round(leadsVal * 0.6);
                                const setRate = '25%';
                                const issuesVal = Math.round(leadsVal * 0.1);
                                const issueRate = '65%';
                                const demosVal = Math.round(leadsVal * 0.5);
                                const demoRate = '50%';
                                const netSoldVal = Math.round(leadsVal * 0.2);
                                const netCloseRate = '20%';
                                const netRevenueVal = Math.round(netSoldVal * avgJobSize);
                                const costMarketingVal = '20%';
                                return (
                                  <TableRow key={vertical}>
                                    <TableCell>{vertical}</TableCell>
                                    <TableCell>{verticalBudget ? `$${verticalBudget.toLocaleString()}` : '—'}</TableCell>
                                    <TableCell>{Number.isFinite(leadsVal) ? leadsVal : '—'}</TableCell>
                                    <TableCell>
                                      {cplVal
                                        ? `$${cplVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                                        : '—'}
                                    </TableCell>
                                    <TableCell>{Number.isFinite(setApptsVal) ? setApptsVal : '—'}</TableCell>
                                    <TableCell>{setRate}</TableCell>
                                    <TableCell>{Number.isFinite(issuesVal) ? issuesVal : '—'}</TableCell>
                                    <TableCell>{issueRate}</TableCell>
                                    <TableCell>{Number.isFinite(demosVal) ? demosVal : '—'}</TableCell>
                                    <TableCell>{demoRate}</TableCell>
                                    <TableCell>{Number.isFinite(netSoldVal) ? netSoldVal : '—'}</TableCell>
                                    <TableCell>{netCloseRate}</TableCell>
                                    <TableCell>{`$${avgJobSize.toLocaleString()}`}</TableCell>
                                    <TableCell>
                                      {Number.isFinite(netRevenueVal) ? `$${netRevenueVal.toLocaleString()}` : '—'}
                                    </TableCell>
                                    <TableCell>{costMarketingVal}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Paper>
                      ) : null}

                      {territoryRows.length > 0 && (
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Box sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
                              <TaskAltIcon color="success" fontSize="small" />
                              <Typography variant="subtitle2">{territoryRows.length} territories captured</Typography>
                            </Stack>
                            <Table size="small" sx={{ tableLayout: 'auto', minWidth: 800 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Territory</TableCell>
                                  <TableCell>Zip count</TableCell>
                                  <TableCell>Population (raw → filtered)</TableCell>
                                  <TableCell>Median income</TableCell>
                                  <TableCell>Median home value</TableCell>
                                  <TableCell>Min income</TableCell>
                                  <TableCell>Min home value</TableCell>
                                  <TableCell>Min SFH%</TableCell>
                                  <TableCell>Min ownership%</TableCell>
                                  <TableCell>Population retained%</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {territoryRows.map((row, idx) => (
                                  <TableRow
                                    key={`${row.territory}-${idx}`}
                                    title={row.sampleZips.length ? `Sample zips: ${row.sampleZips.join(', ')}` : undefined}
                                  >
                                    <TableCell>{row.territory || 'No territory label'}</TableCell>
                                    <TableCell>
                                      {row.zipCount}
                                      {row.zipCountFiltered != null ? ` → ${row.zipCountFiltered}` : ''}
                                    </TableCell>
                                    <TableCell>
                                      {row.population != null ? row.population.toLocaleString() : '—'} {' \u2192 '}{' '}
                                      {row.populationFiltered != null ? row.populationFiltered.toLocaleString() : '—'}
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(row.medianIncomeFiltered ?? row.medianIncome ?? '—')}
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(row.medianHomeValueFiltered ?? row.medianHomeValue ?? '—')}
                                    </TableCell>
                                    <TableCell>{formatCurrency(row.minIncome)}</TableCell>
                                    <TableCell>{formatCurrency(row.minHomeValue)}</TableCell>
                                    <TableCell>{formatPercent(row.minSfh, 0)}</TableCell>
                                    <TableCell>{formatPercent(row.minOwnership, 0)}</TableCell>
                                    <TableCell>{formatPercent(row.populationRetainedPct)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {demographics.meta && demographics.meta.filter_found === false && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                No saved client territory zip filter found. Applying default floors based on zip medians.
                              </Alert>
                            )}
                            {demographics.meta &&
                              demographics.meta.filter_found &&
                              demographics.meta.filter_effective === false && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                Zip filter found but did not exclude any rows (no change vs raw).
                              </Alert>
                            )}
                          </Box>
                        </Paper>
                      )}

                      {/* Zip drill-down intentionally hidden for cleaner view */}
                    </Stack>
                  ) : null}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="outlined">Export CSV</Button>
                    <Button variant="contained" onClick={proceedToOnboarding}>
                      Approve projections → Onboarding
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}

PublicProjectionsPage.displayName = 'PublicProjectionsPage';
export default PublicProjectionsPage;
